/**
 * Snapshot buffer for the guest's view of the world.
 *
 * The guest renders roughly 80 ms in the past so it always has two snapshots to
 * interpolate between. Rendering at the newest snapshot instead would mean
 * stalling every time one is late, which reads as a stutter on the ball -- and
 * the ball is the one thing the host owns outright.
 */
import type { SimState } from '@office/sim';

export const DEFAULT_INTERP_DELAY_MS = 80;

interface Entry {
  /** Local receive time in ms. */
  at: number;
  state: SimState;
}

export interface SnapshotView {
  prev: SimState;
  next: SimState;
  alpha: number;
}

export class SnapshotBuffer {
  private readonly entries: Entry[] = [];
  private newestTick = -1;

  constructor(
    /** How far behind the newest snapshot to render. */
    readonly delayMs: number = DEFAULT_INTERP_DELAY_MS,
    /** How much history to keep. */
    private readonly historyMs: number = 1000,
  ) {}

  get size(): number {
    return this.entries.length;
  }

  get newest(): SimState | null {
    return this.entries.at(-1)?.state ?? null;
  }

  /**
   * Add a snapshot. Out-of-order arrivals (the channel is unordered) and
   * duplicates are dropped rather than reordered into the timeline.
   */
  push(state: SimState, at: number): boolean {
    if (state.tick <= this.newestTick) return false;
    this.newestTick = state.tick;
    this.entries.push({ at, state });
    this.prune(at);
    return true;
  }

  private prune(now: number): void {
    const cutoff = now - this.historyMs;
    // Always keep at least the two entries interpolation needs.
    while (this.entries.length > 2 && (this.entries[0]?.at ?? 0) < cutoff) {
      this.entries.shift();
    }
  }

  /**
   * The pair of snapshots bracketing `now - delayMs`, plus the blend factor.
   * Returns null until the first snapshot arrives; clamps at both ends so a
   * stalled connection freezes on the last known state instead of extrapolating.
   */
  sample(now: number): SnapshotView | null {
    const n = this.entries.length;
    if (n === 0) return null;
    const target = now - this.delayMs;

    const first = this.entries[0]!;
    if (n === 1 || target <= first.at) {
      return { prev: first.state, next: first.state, alpha: 0 };
    }

    const last = this.entries[n - 1]!;
    if (target >= last.at) {
      const before = this.entries[n - 2]!;
      return { prev: before.state, next: last.state, alpha: 1 };
    }

    for (let i = n - 1; i > 0; i--) {
      const prev = this.entries[i - 1]!;
      const next = this.entries[i]!;
      if (target >= prev.at && target <= next.at) {
        const span = next.at - prev.at;
        const alpha = span <= 0 ? 1 : (target - prev.at) / span;
        return { prev: prev.state, next: next.state, alpha };
      }
    }
    return { prev: first.state, next: first.state, alpha: 0 };
  }

  clear(): void {
    this.entries.length = 0;
    this.newestTick = -1;
  }
}
